import React, { useEffect, useState } from 'react';
import { Flex, Heading, Spinner, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { MagnifyingGlass } from 'phosphor-react';
import { GlassCard } from '../../../../components/glass';
import { IAuditoriaFidelidade, useAuditFidelidade } from '../../../../hooks/useAuditFidelidade';
import { RelatorioFidelidade } from './RelatorioFidelidade';

// O auditor roda em BackgroundTask (pos-resposta do /gerar). Aqui a tela de
// sucesso polla o relatorio ate ele ficar pronto, mostrando "vistoriando...".
const POLL_MS = 3000;
// Teto por TEMPO (nao por contagem de poll, que dobraria no StrictMode e nao
// dispara re-render). 90s: casos rapidos carregam em ~3-5s, mas o juiz (Sonnet)
// em contratos grandes ja foi observado levar ~75s (contrato 39) — com 24s o poll
// expirava antes e caia na mensagem confusa, forcando "Atualizar" na mao.
const POLL_MAX_MS = 90000;

// A vistoria recebida é posterior à geração que acabou de rodar?
// `contrato_dt_atualizacao` ausente = back sem o campo -> aceita (comportamento
// anterior), senão o painel travaria em "vistoriando…" para sempre.
const ehDestaGeracao = (a?: IAuditoriaFidelidade | null): boolean => {
  if (!a) return false;
  if (!a.contrato_dt_atualizacao) return true;
  return new Date(a.dt_criacao).getTime() >= new Date(a.contrato_dt_atualizacao).getTime();
};

interface Props {
  contratoId: number;
  // Fase 1 gate: chamado quando o operador confirma a vistoria — o wizard
  // re-busca o download (o back destranca o 409) e habilita o "Baixar DOCX".
  onRevisado?: () => void;
}

export const ValidacaoFidelidadePosGeracao = ({ contratoId, onRevisado }: Props) => {
  const { getAuditoriaContrato } = useAuditFidelidade();
  // Teto por TEMPO via state (re-render correto + imune ao double-mount do
  // StrictMode: o cleanup limpa o 1o timer). `tentativa` reinicia o teto no
  // botao "Atualizar".
  const [expirou, setExpirou] = useState(false);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    setExpirou(false);
    const t = setTimeout(() => setExpirou(true), POLL_MAX_MS);
    return () => clearTimeout(t);
  }, [contratoId, tentativa]);

  const {
    data: recebido,
    isError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['validacao-fidelidade-posgen', contratoId],
    queryFn: () => getAuditoriaContrato(contratoId),
    refetchInterval: query => (ehDestaGeracao(query.state.data) || expirou ? false : POLL_MS),
    staleTime: 0,
  });

  // Só aceita a vistoria DESTA geração. Antes o poll parava no primeiro dado que
  // chegasse — e nessa janela o endpoint devolve a auditoria ANTERIOR, que parece
  // legítima. Medido no contrato 125 (2026-08-11): geração às 21:37:21, painel
  // recebeu a auditoria das 11:44 do mesmo dia, poll parou, e a auditoria correta
  // (21:37:40) nunca foi buscada. O operador confirmava a vistoria — liberando o
  // download — em cima de achados de OUTRO documento.
  //
  // Os dois timestamps vêm do servidor, então isto não depende do relógio do
  // browser. Back sem o campo (deploy antigo) cai no comportamento anterior:
  // aceita o que veio, em vez de travar o painel para sempre.
  const data = ehDestaGeracao(recebido) ? recebido : undefined;
  const processando = !data && !isError && !expirou;

  const renderConteudo = () => {
    if (processando) {
      return (
        <Flex align="center" gap={3} py={2}>
          <Spinner size="sm" color="#7C3AED" thickness="2px" />
          <Text fontSize="sm" color="#64748B">
            A IA está vistoriando a fidelidade deste contrato…
          </Text>
        </Flex>
      );
    }
    if (isError) {
      return (
        <Text fontSize="sm" color="red.500">
          Falha ao carregar a vistoria (acesso restrito a admin/dev).
        </Text>
      );
    }
    if (!data) {
      return (
        <Flex align="center" gap={3} py={2} wrap="wrap">
          <Text fontSize="sm" color="#64748B">
            A vistoria ainda não ficou pronta — em contratos grandes ela pode levar até ~1 min.
            Clique em Atualizar para buscar de novo. (Alterações em v2 não geram relatório de
            fidelidade.)
          </Text>
          <Text
            as="button"
            fontSize="sm"
            color="#7C3AED"
            fontWeight="600"
            onClick={() => {
              setTentativa(n => n + 1);
              refetch();
            }}
          >
            {isFetching ? 'Atualizando…' : 'Atualizar'}
          </Text>
        </Flex>
      );
    }
    return <RelatorioFidelidade auditoria={data} contratoId={contratoId} onRevisado={onRevisado} />;
  };

  return (
    <GlassCard intensity="strong" p={5}>
      <Flex align="center" gap={2} mb={data ? 4 : 0}>
        <MagnifyingGlass size={20} color="#7C3AED" weight="duotone" />
        <Heading fontSize="md" color="#0F172A">
          Validação de Fidelidade
        </Heading>
        <Text fontSize="xs" color="#94A3B8">
          (vistoria da IA — vigente × render)
        </Text>
      </Flex>

      {renderConteudo()}
    </GlassCard>
  );
};

export default ValidacaoFidelidadePosGeracao;
